import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';
import config from 'app.config';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { AppleLogoSvg } from 'pages/common/components/svgs/AppleLogoSvg';
import { FacebookLogoSvg } from 'pages/common/components/svgs/FacebookLogoSvg';
import { GoogleLogoSvg } from 'pages/common/components/svgs/GoogleLogoSvg';

import { useFunnelStorage } from 'pages/funnel/storage/Storage';
import { selectAnyProviderSelected, selectRequestedAppointment } from 'pages/legacy-checkout/connect/selectors';
import { SSO_APPOINTMENT, SSO_PROVIDERS, SSO_REDIRECT } from 'pages/legacy-checkout/helpers/checkout-constants';

import { useRedirectionService } from 'services/RedirectionService';

import { GtmDataLayerEvent, trackGtmDataLayerEvent } from 'utils/analytics';

import { SsoButton } from './SsoButton/SsoButton';
import { SsoButtonsWrapper } from './SsoButtons-styled-components';

// Not sure if this is worth having an exportable type?
type ValueType<T> = T[keyof T];

const analytics = new Analytics();

interface LoginWithSso {
  (ssoProvider: ValueType<typeof SSO_PROVIDERS>): void;
}

const ssoButtons = [
  {
    name: 'Google',
    logo: <GoogleLogoSvg />,
    createOnClick: (loginWithSSO: LoginWithSso) => {
      return () => loginWithSSO(SSO_PROVIDERS.GOOGLE);
    },
  },
  {
    name: 'Facebook',
    logo: <FacebookLogoSvg />,
    createOnClick: (loginWithSSO: LoginWithSso) => {
      return () => loginWithSSO(SSO_PROVIDERS.FACEBOOK);
    },
  },
  {
    name: 'Apple',
    logo: <AppleLogoSvg />,
    createOnClick: (loginWithSSO: LoginWithSso) => {
      return () => loginWithSSO(SSO_PROVIDERS.APPLE);
    },
  },
];

export const SsoButtons = () => {
  const { nextStepUrl } = useNextCheckoutStep();
  const { origin } = window.location;

  const [ssoProviderLoading, setSsoProviderLoading] = useState<ValueType<typeof SSO_PROVIDERS> | null>(null);
  const funnelStorage = useFunnelStorage();

  const requestedAppointment = useSelector(selectRequestedAppointment);
  const anyProviderSelected = useSelector(selectAnyProviderSelected);
  const redirectionService = useRedirectionService();

  const loginWithSSO: LoginWithSso = async (ssoProvider) => {
    if (ssoProviderLoading) {
      return;
    }

    setSsoProviderLoading(ssoProvider);

    trackGtmDataLayerEvent(
      GtmDataLayerEvent.APPOINTMENT_CHECKOUT_ACCOUNT_CREATED,
    );
    analytics.trackClick({
      pageType: analytics.pageTypes.FORM,
      pageName: 'form-booking-account',
      name: 'form-booking',
      section: 'account',
      content: {
        [`sso-${ssoProvider}`]: 'true',
        ssoProviderName: ssoProvider,
      },
      practiceId: requestedAppointment.provider?.clinic,
      providerId: requestedAppointment.providerId,
      funnelConfigId: funnelStorage?.config?.id,
    });

    await analytics.waitForQueue();

    // Create a URL object for the redirect URL
    const redirectURL = new URL(`${origin}${nextStepUrl}`);

    // Add query parameters using URLSearchParams
    redirectURL.searchParams.set(SSO_REDIRECT, 'true');
    redirectURL.searchParams.set(SSO_APPOINTMENT.PROVIDER, requestedAppointment.providerId);

    if (requestedAppointment.slotId) {
      redirectURL.searchParams.set(SSO_APPOINTMENT.SLOT, requestedAppointment.slotId);
    }

    const anyProviderSelectedString = (anyProviderSelected || '').toString();
    redirectURL.searchParams.set(SSO_APPOINTMENT.ANY_PROVIDER, anyProviderSelectedString);

    // Create SSO authorization URL with the encoded redirect URI
    const ssoUrl = new URL(`/auth/${ssoProvider}`, config.API_URL);
    ssoUrl.searchParams.set('redirectUri', redirectURL.toString());

    // Send them on their way
    setSsoProviderLoading(null);
    redirectionService.redirect({
      url: ssoUrl.toString(),
      reason: `sso-${ssoProvider}`,
    });
  };

  return (
    <SsoButtonsWrapper>
      {ssoButtons.map(button => (
        <SsoButton
          key={button.name}
          name={button.name}
          logo={button.logo}
          onClick={button.createOnClick(loginWithSSO)}
        />
      ))}
    </SsoButtonsWrapper>
  );
};

